import _extends from'@babel/runtime/helpers/extends';import _objectSpread from'@babel/runtime/helpers/objectSpread2';import _toConsumableArray from'@babel/runtime/helpers/toConsumableArray';import _slicedToArray from'@babel/runtime/helpers/slicedToArray';import _objectWithoutProperties from'@babel/runtime/helpers/objectWithoutProperties';import React,{useMemo,useRef,useEffect,useCallback}from'react';import PropTypes from'prop-types';import Joi from'joi';import {TextInput}from'@manulife/mux';import {filterErrorsForComponent,ciamErrorsToMuxErrors}from'../../utils/functions.js';import {ErrorShape}from'../../utils/propTypes.js';import muxCustomStyles from'../../customStyles/index.js';import useValidation from'../../hooks/useValidation.js';import CheckList from'../CheckList/CheckList.js';var wrapRefObjectAsFunctionalRef = function wrapRefObjectAsFunctionalRef(ref) {
  return function (element) {
    // When the TextInput has a mask, it renders an InputMask from react-input-mask.
    //   This does accept a ref prop (inputRef) but it expects it to be a functional ref
    //   and does not work with the newer ref object from useRef.
    //   Wrapping the object in a function supports it with and without masks.
    if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = element;
    }
  };
};
var FormTextInput = function FormTextInput(_ref) {
  var id = _ref.id,
    label = _ref.label,
    value = _ref.value,
    onChange = _ref.onChange,
    customStyle = _ref.customStyle,
    errors = _ref.errors,
    validations = _ref.validations,
    validationsCombinedLabel = _ref.validationsCombinedLabel,
    disabled = _ref.disabled,
    formattedValue = _ref.formattedValue,
    errorValues = _ref.errorValues,
    labelForValidation = _ref.labelForValidation,
    isCheckList = _ref.isCheckList,
    defaultHidden = _ref.defaultHidden,
    resetValidationMsgOnChange = _ref.resetValidationMsgOnChange,
    autoFocus = _ref.autoFocus,
    disableValidateOnBlur = _ref.disableValidateOnBlur,
    rest = _objectWithoutProperties(_ref, ["id", "label", "value", "onChange", "customStyle", "errors", "validations", "validationsCombinedLabel", "disabled", "formattedValue", "errorValues", "labelForValidation", "isCheckList", "defaultHidden", "resetValidationMsgOnChange", "autoFocus", "disableValidateOnBlur"]);
  var filteredErrors = useMemo(function () {
    return filterErrorsForComponent(errors, id);
  }, [errors, id]);
  var muxErrors = useMemo(function () {
    return ciamErrorsToMuxErrors(filteredErrors, errorValues);
  }, [filteredErrors, errorValues]);
  var inputRef = useRef();
  var _useValidation = useValidation(validations, formattedValue || value, labelForValidation || label, validationsCombinedLabel, inputRef, isCheckList),
    _useValidation2 = _slicedToArray(_useValidation, 5),
    visibleValidationErrors = _useValidation2[0],
    setShowValidationErrors = _useValidation2[1],
    allValidationErrors = _useValidation2[2],
    showCheckList = _useValidation2[3],
    setShowCheckList = _useValidation2[4];
  var combinedFieldErrors = useMemo(function () {
    return [].concat(_toConsumableArray(muxErrors), _toConsumableArray(visibleValidationErrors));
  }, [muxErrors, visibleValidationErrors]);

  // Update the field validity when it changes
  useEffect(function () {
    var hasError = combinedFieldErrors.length > 0 || allValidationErrors.length > 0;
    inputRef.current.setCustomValidity(hasError ? 'error' : '');
  }, [combinedFieldErrors, allValidationErrors]);
  useEffect(function () {
    if (muxErrors && muxErrors.length > 0) {
      inputRef.current.focus();
    }
  }, [muxErrors]);
  useEffect(function () {
    if (disabled) {
      setShowValidationErrors(false);
      setShowCheckList(false);
    }
  }, [disabled, setShowValidationErrors, setShowCheckList]);

  // When the form that this element belongs to is submited, show validation errors
  useEffect(function () {
    var input = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
    var form = input === null || input === void 0 ? void 0 : input.form;
    if (form) {
      var handleSubmit = function handleSubmit() {
        setShowValidationErrors(true);
        setShowCheckList(true);
      };
      form.addEventListener('submit', handleSubmit);
      return function () {
        return form.removeEventListener('submit', handleSubmit);
      };
    }
    return undefined;
  }, [inputRef, setShowValidationErrors, setShowCheckList]);
  useEffect(function () {
    if (autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  var handleChange = useCallback(function (newVal, e) {
    if (resetValidationMsgOnChange) {
      setShowValidationErrors(false);
    }
    onChange(newVal, e);
    setShowCheckList(true);
  }, [resetValidationMsgOnChange, setShowValidationErrors, setShowCheckList, onChange]);
  var handleFocus = useCallback(function () {
    return setShowCheckList(true);
  }, [setShowCheckList]);
  var handleBlur = useCallback(function () {
    if (!disableValidateOnBlur) {
      setShowValidationErrors(true);
    }
  }, [setShowValidationErrors, disableValidateOnBlur]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, _extends({
    ref: wrapRefObjectAsFunctionalRef(inputRef),
    id: id,
    label: label,
    value: value,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    customStyle: _objectSpread(_objectSpread({}, muxCustomStyles.textInput), customStyle),
    errors: combinedFieldErrors,
    disabled: disabled,
    callouts: true
  }, rest)), isCheckList && showCheckList && /*#__PURE__*/React.createElement(CheckList, {
    id: "checklist-".concat(id),
    errors: allValidationErrors,
    checkList: validationsCombinedLabel,
    value: value,
    defaultHidden: defaultHidden
  }));
};
process.env.NODE_ENV !== "production" ? FormTextInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelForValidation: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  customStyle: PropTypes.shape({}),
  errors: PropTypes.arrayOf(ErrorShape),
  errorValues: PropTypes.shape({}),
  validations: PropTypes.instanceOf(Joi.constructor),
  validationsCombinedLabel: PropTypes.string,
  disabled: PropTypes.bool,
  formattedValue: PropTypes.string,
  isCheckList: PropTypes.bool,
  defaultHidden: PropTypes.arrayOf(PropTypes.string),
  resetValidationMsgOnChange: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disableValidateOnBlur: PropTypes.bool
} : void 0;
FormTextInput.defaultProps = {
  customStyle: {},
  errors: [],
  errorValues: {},
  validations: {},
  validationsCombinedLabel: {},
  disabled: false,
  formattedValue: '',
  labelForValidation: undefined,
  isCheckList: false,
  defaultHidden: [],
  resetValidationMsgOnChange: false,
  autoFocus: false,
  disableValidateOnBlur: false
};
var FormTextInput$1 = FormTextInput;export{FormTextInput$1 as default};